/* @ngInject */
function TariffFactory($http, $filter, $rootScope, Utils, Notification) {
  class Tariff {
    constructor(data) {
      this.description = null;
      this.unit = null;
      this.price = null;
      this.costs = [];
      this.createdAt = null;
      this.updatedAt = null;

      if (data) this.setData(data);
    }

    setData(data) {
      this._id = data._id ? data._id : undefined;
      this.description = data.description;
      this.unit = data.unit;
      this.price = data.price;
      this._updateCosts(data.costs || []);
      this.createdAt = data.createdAt;
      this.updatedAt = data.updatedAt;
      this.updateMargin();
    }

    setDataFromQuoteLine(data) {
      const currencyTo = $rootScope.organization.settings.currency;
      const rates = $rootScope.organization.exchangeRates.rates;

      this._id = data.from || undefined;
      this.description = data.description;
      this.unit = data.unit;
      this.price = data.price;
      this.costs = [];

      if (data.currency !== currencyTo) {
        const priceBeforeRecalculate = this.price;
        this.price = Utils.getCurrencyNumberConverted(rates, data.currency, currencyTo, this.price);
        this._checkPriceExceedMax(priceBeforeRecalculate, currencyTo);
      }

      for (let i = 0; i < data.costs.length; i++) {
        this.costs.push({
          cost: {
            _id: data.costs[i].from || data.costs[i]._id,
            currency: data.costs[i].currency,
            name: data.costs[i].name,
            unit: data.costs[i].unit,
            price: data.costs[i].price,
          },
          quantity: data.costs[i].amount,
        });
      }
    }

    static getResourceUrl() {
      if (!$rootScope.organization) throw new Error("Organization was not loaded yet.");
      return `${process.env.SERVER_URL}/api/secure/organization/${$rootScope.organization.slug}/tariffs`;
    }

    static query(params) {
      return $http
        .get(Tariff.getResourceUrl(), { params })
        .then((response) => response.data.map((tariffData) => new Tariff(tariffData)));
    }

    static getCount(params) {
      return $http.get(`${Tariff.getResourceUrl()}/count`, { params }).then((response) => response.data);
    }

    addCost(cost) {
      if (this._containsCost(cost)) return;
      this.costs.push({ cost: cost, quantity: 1 });

      if (!this.price && !this.margin) {
        this.margin = 0;
        this.updatePrice();
      } else if (!this.price && this.margin) this.updatePrice();
      else if (this.price && !this.margin) this.updateMargin();
      else if (this.price && this.margin) this.updatePrice();
    }

    removeCost(index) {
      this.costs.splice(index, 1);
      if (this.price && this.costs.length > 0) this.updatePrice();
    }

    getMargin() {
      return Utils.calculateMarginFromPrice(this.getTotalCost(), this.price || 0, 0);
    }

    updateMargin() {
      this.margin = Utils.calculateMarginFromPrice(this.getTotalCost(), this.price || 0);
    }

    updatePrice() {
      this.price = Utils.calculatePriceFromMargin(this.getTotalCost(), this.margin || 0);
    }

    recalculatePrice(oldCurrency, newCurrency) {
      const priceBeforeRecalculate = this.price;
      const rates = $rootScope.organization.exchangeRates.rates;
      this.price = Utils.getCurrencyNumberConverted(rates, oldCurrency, newCurrency, this.price);
      this._checkPriceExceedMax(priceBeforeRecalculate, newCurrency);
    }

    getTotalCost() {
      const currencyTo = $rootScope.organization.settings.currency;
      const rates = $rootScope.organization.exchangeRates.rates;
      let totalCost = 0;
      let cost = 0;

      for (let i = 0; i < this.costs.length; i++) {
        const price = Number(this.costs[i].cost.price) || 0;
        const quantity = Number(this.costs[i].quantity) || 0;
        cost = price * quantity;

        if (this.costs[i].cost.currency === currencyTo) totalCost += cost;
        else totalCost += Utils.getCurrencyNumberConverted(rates, this.costs[i].cost.currency, currencyTo, cost);
      }

      return totalCost;
    }

    isNew() {
      return !this._id;
    }

    save() {
      return this.isNew() ? this.create() : this.update();
    }

    saveFromQuote() {
      return this.isNew() ? this.createFromQuote() : this.updateFromQuote();
    }

    create() {
      if (!this.isNew()) throw new Error("The tariff is already created");

      return $http.post(Tariff.getResourceUrl(), this).then((response) => angular.extend(this, response.data));
    }

    createFromQuote() {
      if (!this.isNew()) throw new Error("The tariff is already created");

      return $http
        .post(`${Tariff.getResourceUrl()}/from-quote`, this)
        .then((response) => angular.extend(this, response.data));
    }

    update() {
      if (this.isNew()) throw new Error("Can not update a tariff that has not been created");

      return $http
        .put(`${Tariff.getResourceUrl()}/${this._id}`, this)
        .then((response) => angular.extend(this, response.data));
    }

    updateFromQuote() {
      if (this.isNew()) throw new Error("Can not update a tariff that has not been created");

      return $http
        .put(`${Tariff.getResourceUrl()}/${this._id}/from-quote`, this)
        .then((response) => angular.extend(this, response.data));
    }

    delete() {
      if (this.isNew()) throw new Error("Can not delete a tariff that has not been created");

      return $http.delete(`${Tariff.getResourceUrl()}/${this._id}`).then(() => true);
    }

    containsAnyCost() {
      return this.costs.length > 0;
    }

    _checkPriceExceedMax(priceBeforeRecalculate, newCurrency) {
      if (this.price > 9999999999.99) {
        this.price = priceBeforeRecalculate;
        Notification.error({
          title: "¡Ups!",
          message: $filter("trans")("tariffFactory.checkPriceExceedMax.error", [this.description, newCurrency]),
          delay: "persist",
        });
      }
    }

    _updateCosts(costsData) {
      for (let i = 0; i < costsData.length; i++) {
        if (this.costs[i]) {
          this.costs[i].cost = costsData[i].cost;
          this.costs[i].quantity = costsData[i].quantity;
        } else {
          this.costs.push({
            cost: costsData[i].cost,
            quantity: costsData[i].quantity,
          });
        }
      }

      if (this.costs.length > costsData.length) {
        this.costs = this.costs.slice(0, costsData.length);
      }
    }

    _containsCost(cost) {
      return this.costs.some((item) => angular.equals(item.cost._id, cost._id));
    }
  }

  return Tariff;
}

export default TariffFactory;
